import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { toast } from "sonner";

export default function UsagePage() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [consumption, setConsumption] = useState([]);
  const [rec, setRec] = useState({ metric: "users", delta: 1 });

  useEffect(() => {
    api.get("/v1/tenants").then((r) => setTenants(r.data.data.items)).catch(() => {});
  }, []);

  const load = async (id) => {
    setTenantId(id);
    if (!id) { setConsumption([]); return; }
    try { setConsumption((await api.get(`/v1/usage/tenant/${id}/consumption`)).data.data); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const record = async () => {
    try {
      const r = await api.post("/v1/usage/record", { tenantId, metric: rec.metric, delta: Number(rec.delta) });
      const s = r.data.data.status;
      toast[s === "blocked" ? "error" : s === "warning" ? "warning" : "success"](`Recorded. Status: ${s}`);
      load(tenantId);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const barColor = (pct) => (pct == null ? "bg-slate-300" : pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-primary");

  return (
    <div data-testid="usage-page">
      <PageHeader title="Usage Governance" subtitle="Monitor consumption vs plan limits, with soft/hard thresholds" />
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-sm font-medium text-slate-700 mb-1">Select Tenant</label>
          <select value={tenantId} onChange={(e) => load(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="usage-tenant-select">
            <option value="">Choose a tenant…</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName} ({t.tenantCode})</option>)}
          </select>
        </div>
        {tenantId && (
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Simulate usage</label>
              <select value={rec.metric} onChange={(e) => setRec({ ...rec, metric: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="usage-metric">
                {consumption.map((c) => <option key={c.metric}>{c.metric}</option>)}
              </select>
            </div>
            <input type="number" value={rec.delta} onChange={(e) => setRec({ ...rec, delta: e.target.value })} className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="usage-delta" />
            <button onClick={record} className="px-3 py-2 text-sm rounded-lg bg-primary text-white font-medium" data-testid="usage-record">Record</button>
          </div>
        )}
      </div>

      {tenantId && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {consumption.map((c) => (
            <div key={c.metric} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid={`usage-${c.metric}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-700 capitalize">{c.metric}</span>
                <span className="text-xs text-slate-400">{c.hardLimit === -1 ? "Unlimited" : `${c.used} / ${c.hardLimit ?? "—"}`}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${barColor(c.usagePct)}`} style={{ width: `${Math.min(c.usagePct || 0, 100)}%` }} />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">{c.usagePct != null ? `${c.usagePct}% used` : "No limit set"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
